import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useReactToPrint } from "react-to-print";
import moment from 'moment';

import { AppConfig } from '../config/appConfig';

import { IMaterialSummary } from '../types/material';
import { IPinkSheetData, IShipmentExpense, IPinkSheetLoad, IPinkSheetCustomer, IPinkSheetVendor, IShipmentArap, IShipmentLight, IShipmentMaterial } from '../types/shipment';

import { UtilityService } from '../services/utilityService';

import { Decimal } from '../decimal';
import Json from './json';
import Spinner from './spinner';

import '../styles/pinkSheet.scss';

export interface IPinkSheetProps {
	shipment: IShipmentLight;
	materials: IShipmentMaterial[];
	isLoadingMaterials: boolean;
	pinkSheetData: IPinkSheetData;
	isLoadingPinkSheetData: boolean;
	showHwiNumberInHeader?: boolean;
	pageNumber?: string;
	pagesTotal?: string;
}

const PinkSheet: React.FunctionComponent<IPinkSheetProps> = ({ shipment, materials, isLoadingMaterials, pinkSheetData, isLoadingPinkSheetData, showHwiNumberInHeader = false, pageNumber, pagesTotal }) => {
	const [shipmentExpenses, setShipmentExpenses] = useState<IShipmentExpense[]>([]);
	const [shipmentArap, setShipmentArap] = useState<IShipmentArap | undefined>();
	const [totalExpenseAmount, setTotalExpenseAmount] = useState(0);
	const [totalMaterialCostCredit, setTotalMaterialCostCredit] = useState(0);
	const [materialSummaryList, setMaterialSummaryList] = useState<IMaterialSummary[]>([]);
	const [totalMaterialWeightLbs, setTotalMaterialWeightLbs] = useState(0);
	const [totalTareWeightLbs, setTotalTareWeightLbs] = useState(0);
	const [totalQuantity, setTotalQuantity] = useState(0);

	const [vendor, setVendor] = useState<IPinkSheetVendor | undefined>();
	const [customer, setCustomer] = useState<IPinkSheetCustomer | undefined>();
	const [load, setLoad] = useState<IPinkSheetLoad | undefined>();
	const [showPinkSheet, setShowPinkSheet] = useState(false);

	const ref = useRef(null);

	useEffect(() => {
		if (!shipment || !pinkSheetData) return;
		const expenses: IShipmentExpense[] = [];
		let totalExpenseAmount = 0;

		pinkSheetData.shipment.expenses.map((item) => {
			let e: IShipmentExpense = {
				amount: item.isCost ? item.amount * -1 : item.amount,
				id: item.id,
				shipmentId: item.shipmentId,
				typeId: item.typeId,
				typeName: item.typeName,
				notes: item.notes,
				isCost: item.isCost
			}

			expenses.push(e);
			if (item.isCost) {
				totalExpenseAmount = totalExpenseAmount - item.amount;
			}
			else {
				totalExpenseAmount = totalExpenseAmount + item.amount;
			}
		})
		setShipmentExpenses(expenses.sort((a, b) => UtilityService.compareStrings(a.typeName, b.typeName)));
		setTotalExpenseAmount(totalExpenseAmount);
	}, [shipment, pinkSheetData])

	useEffect(() => {
		if (!pinkSheetData) return;
		setShipmentArap(pinkSheetData.shipment.arap);
		setLoad(pinkSheetData.load);
		setVendor(pinkSheetData.vendor);
		setCustomer(pinkSheetData.customer);

	}, [pinkSheetData])

	useEffect(() => {
		let summaryList: IMaterialSummary[] = [];
		let totalCredit: number = 0;
		let totalMaterialWeight: number = 0;
		let totalTareWeight: number = 0;
		let totalQuantity: number = 0;
		if (!materials) return;

		materials.map((listItem) => {
			const itemNetMaterialWeightInPounds = listItem.vendorAmount;
			const itemContainerWeightInPounds = listItem.containerWeight * (listItem.quantity ?? 1);

			const results = summaryList.filter(
				(summaryItem) =>
					listItem.vendorMaterialId == summaryItem.materialId &&
					listItem.vendorMaterialPriceProfile == summaryItem.materialProfile &&
					listItem.plantUOMAbbreviation == summaryItem.uomAbbreviation);
			if (results.length == 0) {
				summaryList.push({
					"materialCategory": listItem.vendorMaterialPriceCategory,
					"quantity": listItem.quantity ?? 1,
					"materialName": listItem.vendorMaterialName,
					"materialId": listItem.vendorMaterialId,
					"materialProfile": listItem.vendorMaterialPriceProfile,
					"materialWeightLbs": itemNetMaterialWeightInPounds,
					"tareWeightLbs": listItem.vendorMaterialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds,
					'isGrossWeightMaterial': listItem.vendorMaterialPriceIsGrossWeightMaterial,
					"uomAbbreviation": listItem.vendorUOMAbbreviation,
					"creditTotal": listItem.vendorCreditTotal,
				})
			}
			else {
				results[0].quantity += listItem.quantity ?? 1;
				results[0].materialWeightLbs += itemNetMaterialWeightInPounds;
				results[0].tareWeightLbs += listItem.vendorMaterialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds;
				results[0].creditTotal += listItem?.vendorCreditTotal;
			}
		});

		summaryList.map((item) => {
			totalCredit += item?.creditTotal;
			totalMaterialWeight += convertToPounds(item?.materialWeightLbs, item?.uomAbbreviation);;
			totalTareWeight += item?.tareWeightLbs;
			totalQuantity += item?.quantity;
		})

		setTotalMaterialCostCredit(totalCredit);
		setTotalMaterialWeightLbs(totalMaterialWeight);
		setTotalTareWeightLbs(totalTareWeight);
		setTotalQuantity(totalQuantity);
		setMaterialSummaryList(summaryList.sort((a, b) => UtilityService.compareTwoFields(a, b, 'materialName', 'materialProfile', 'string', 'number')));
	}, [materials])

	const convertToPounds = (amount: number, uom: string) => {
		switch (uom) {
			case "units": {
				return 0;
			}
			case "transportation": {
				return 0;
			}
			case "lbs": {
				return amount;
			}
			case "gal": {
				return amount * 7.46;
			}
			case "tn": {
				return amount * 2000;
			}
			case "lt": {
				return amount * 2240;
			}
			case "mt": {
				return amount * 2204.62;
			}
			case "kg": {
				return amount * 2.20462;
			}
			default: {
				return amount;
			}
		}
	}

	const print = useReactToPrint({
		contentRef: ref,
		documentTitle: `PinkSheet`,
	});

	const handlePrintClick = () => {
		print();
	}

	useEffect(() => {
		let show = true;
		if (!shipment || isLoadingMaterials || isLoadingPinkSheetData) {
			show = false;
		}

		setShowPinkSheet(show);
	}, [shipment, isLoadingMaterials, isLoadingPinkSheetData])

	return (
		<>
			<Spinner dependency={!showPinkSheet} text={<>Loading Pink Sheet <strong>{shipment?.hwiNumber}</strong>...</>}>
				<div className="hwi-pink-sheet">
					{showHwiNumberInHeader &&
						<span className="page-meta">
							<span className="hwi-number">
								{shipment?.hwiNumber}
							</span>
							{(pageNumber != undefined) &&
								<span className="page-index"> {pageNumber}{pagesTotal != undefined && `/${pagesTotal}`}</span>
							}
						</span>
					}

					<div className="print-button">
						<Button onClick={handlePrintClick}>Print Pink Sheet</Button>
					</div>
					<div className="wrapper" ref={ref}>
						<section className="content">
							<div className="header">
								<div className="col-a">
									<div>To:___________________________________________ </div>
									<div>From:________________________________________ </div>
								</div>
								<div className="col-b">
									<div>HWI Environmental Technologies</div>
									<div>Phone: {"(314) 842-8383"}</div>
									<div>Fax: {"(314) 842-7772"}</div>
								</div>
							</div>
							<div className="title">
								<p>HWI Environmental Technologies<br></br>Shipping Request Form</p>
							</div>
							<div className="details">
								<div className="row">
									<div className="label">Date:</div>
									<div className="value">{moment(shipment?.pickupDate).format("MM/DD/yyyy")}</div>
								</div>
								<div className="row">
									<div className="label">Company Name:</div>
									<div className="value">{customer?.name}</div>
								</div>
								<div className="row">
									<div className="label">Site Address:</div>
									<div className="value">{customer?.address}</div>
								</div>
								<div className="row">
									<div className="label">Site City:</div>
									<div className="value">{customer?.city}</div>
								</div>
								<div className="row">
									<div className="label">Site State:</div>
									<div className="value">{customer?.state}</div>
								</div>
								<div className="row">
									<div className="label">Site Phone:</div>
									<div className="value">{customer?.phone}</div>
								</div>

								<div className="row">
									<div className="label">Facility Name:</div>
									<div className="value">{vendor?.name}</div>
								</div>
								<div className="row">
									<div className="label">Facility Address:</div>
									<div className="value">{vendor?.address}</div>
								</div>
								<div className="row">
									<div className="label">Facility City:</div>
									<div className="value">{vendor?.city}</div>
								</div>
								<div className="row">
									<div className="label">Facility State:</div>
									<div className="value">{vendor?.state}</div>
								</div>
								<div className="row">
									<div className="label">Facility Phone:</div>
									<div className="value">{vendor?.phone}</div>
								</div>
								<div className="row">
									<div className="label">HWI #:</div>
									<div className="value">{shipment?.hwiNumber}  <i>*Please use this reference number on all paperwork to HWI.</i></div>
								</div>
								<div className="row">
									<div className="label">Vendor Invoice #:</div>
									<div className={`value ${!shipmentArap?.vendorInvoice ? "unavailable" : ""}`}>{shipmentArap?.vendorInvoice || "No Vendor Invoice was provided."}</div>
								</div>
								<div className="row">
									<div className="label">Transporter Invoice #:</div>
									<div className={`value ${!shipmentArap?.transporterInvoice ? "unavailable" : ""}`}>{shipmentArap?.transporterInvoice || "No Transporter Invoice was provided."}</div>
								</div>
								<div className="row">
									<div className="label">Batch #:</div>
									<div className={`value ${!shipment?.batchId ? "unavailable" : ""}`}>{shipment.batchId ? shipment.batchId : "This shipment is not part of a batch."}</div>
								</div>
								<div className="row">
									<div className="label">Manifest #:</div>
									<div className={`value ${!shipment?.manifestNumbers?.length ? "unavailable" : ""}`}>{shipment?.manifestNumbers?.length ? shipment.manifestNumbers.join(", ") : "No manifest numbers were provided."}</div>
								</div>
								<div className="row">
									<div className="label">Seal #:</div>
									<div className="value">{load?.sealNumber}</div>
								</div>
								<div className="row">
									<div className="label">Trailer #:</div>
									<div className="value">{load?.trailerNumber}</div>
								</div>
								<div className="row">
									<div className="label">Notes:</div>
									<div className="value">{shipmentArap?.invoiceNotes}</div>
								</div>
							</div>

							<div className="materials">
								<div className="row header">
									<div className="col qty">QTY</div>
									<div className="col material-col">Material Description</div>
									<div className="col weight text-right">Material</div>
									<div className="col tare">Tare</div>
									<div className="col total">Total</div>
									<div className="col credit">Credit</div>
								</div>
								{materialSummaryList.map((item, index) => (
									<div className="row item" key={index}>
										<div className="col qty">{item?.quantity}</div>
										<div className="col material-col">{item?.materialName}</div>
										<div className="col weight text-right">{item?.materialWeightLbs?.toLocaleString() ?? 'N/A'} {item?.uomAbbreviation}</div>
										<div className="col tare">{item?.isGrossWeightMaterial ? "*" : `${item?.tareWeightLbs?.toLocaleString() ?? 'N/A'} lbs`}</div>
										<div className="col total">{(convertToPounds(item?.materialWeightLbs ?? 0, item?.uomAbbreviation) + (item?.isGrossWeightMaterial ? 0 : (item?.tareWeightLbs ?? 0)))?.toLocaleString()} lbs</div>
										<div className="col credit">{new Decimal(item?.creditTotal ?? 0).toFixed(2)}</div>
									</div>
								))}
								{shipmentExpenses.map((item, index) => (
									<div className="row item" key={index}>
										<div className="col qty"></div>
										<div className="col material-col">{item?.typeName}</div>
										<div className="col weight text-right">(expense)</div>
										<div className="col tare"></div>
										<div className="col total"></div>
										<div className="col credit">{new Decimal(item?.amount ?? 0).toFixed(2)}</div>
									</div>
								))}
								<div className="row totals">
									<div className="col qty">{totalQuantity}</div>
									<div className="col material-col">Totals</div>
									<div className="col weight text-right">{totalMaterialWeightLbs?.toLocaleString()} lbs</div>
									<div className="col tare">{totalTareWeightLbs?.toLocaleString()} lbs</div>
									<div className="col total">{(totalMaterialWeightLbs + totalTareWeightLbs)?.toLocaleString()} lbs</div>
									<div className="col credit">{new Decimal(totalMaterialCostCredit + totalExpenseAmount).toFixed(2)}</div>
								</div>
								{materialSummaryList?.some(m => m.isGrossWeightMaterial) &&
									<div className="gross-weight-materials-warning">
										<p>{AppConfig.grossWeightMaterialMessage}</p>
									</div>
								}
							</div>
						</section>
					</div>
					{/* <Json data={materialSummaryList} title="Summary:"></Json>*/}
					{/* <Json data={load} title="Load:"></Json>  */}
				</div>
			</Spinner>
		</>
	)
}

export default PinkSheet;